import { AuthenticationRemoteDatasource } from "@authentication/data/datasources/authentication-remote.datasource";
import { AuthenticationStorageDatasource } from "@authentication/data/datasources/authentication-storage.datasource";
import { LoginUserMapper } from "@authentication/data/mappers/login-user.mapper";
import type { LoginError } from "@authentication/domain/errors/login/login.error";
import type { LogoutError } from "@authentication/domain/errors/logout/logout.error";
import { NoLoggedUserError } from "@authentication/domain/errors/logout/no-logged-user.error";
import type { LogoutMode } from "@authentication/domain/models/logout.model";
import type { AuthenticationRepository } from "@authentication/domain/repositories/authentication.repository";
import type { UserSessionRepository } from "@authentication/domain/repositories/user-session.repository";
import { authenticationTypes } from "@authentication/root/authentication-types.di";
import type { EventBusRepository, Nullable } from "@iot-platform/core";
import {
    coreTypes,
    DoneLogoutEventBus,
    FallbackError,
    HttpUnauthorizedError,
    ValidationError,
} from "@iot-platform/core";
import { inject, injectable } from "inversify";
import type { ResultAsync } from "neverthrow";
import { okAsync } from "neverthrow";

@injectable()
export class AuthenticationImplRepository implements AuthenticationRepository {
    constructor(
        @inject(AuthenticationRemoteDatasource)
        private readonly authenticationRemoteDatasource: AuthenticationRemoteDatasource,
        @inject(AuthenticationStorageDatasource)
        private readonly authenticationStorageDatasource: AuthenticationStorageDatasource,
        @inject(coreTypes.repositories.EventBusRepository)
        private readonly eventBusRepository: EventBusRepository,
        @inject(authenticationTypes.repositories.UserSessionRepository)
        private readonly userSessionRepository: UserSessionRepository,
        @inject(LoginUserMapper)
        private readonly loginUserMapper: LoginUserMapper,
    ) {}

    get hasAccessToken(): boolean {
        const token = this.getAccessToken();

        return token !== null && Boolean(token.length);
    }

    getAccessToken(): Nullable<string> {
        return this.authenticationStorageDatasource.getAccessToken();
    }

    login(
        email: string,
        password: string,
        rememberSession = true,
    ): ResultAsync<true, LoginError> {
        return this.authenticationRemoteDatasource
            .login(email, password)
            .map((loggedUser) => {
                const authenticatedUser =
                    this.loginUserMapper.mapUser(loggedUser);
                this.userSessionRepository.setUser(authenticatedUser);
                this.authenticationStorageDatasource.saveAccessToken(
                    loggedUser.token,
                    rememberSession,
                );
                return true as const;
            });
    }

    logout(mode: LogoutMode): ResultAsync<true, LogoutError> {
        const logoutOperation =
            mode === "complete"
                ? this.authenticationRemoteDatasource.logout()
                : okAsync(true);

        return logoutOperation
            .map(() => {
                this.authenticationStorageDatasource.deleteAccessToken();
                this.userSessionRepository.setUser(null);
                this.eventBusRepository.emit(new DoneLogoutEventBus());
                return true as const;
            })
            .mapErr((error) => {
                this.authenticationStorageDatasource.deleteAccessToken();
                this.userSessionRepository.setUser(null);
                this.eventBusRepository.emit(new DoneLogoutEventBus());
                if (error instanceof HttpUnauthorizedError)
                    return new NoLoggedUserError();
                return new FallbackError();
            });
    }

    requestMailToResetPassword(
        email: string,
    ): ResultAsync<true, ValidationError | FallbackError> {
        return this.authenticationRemoteDatasource.requestMailToResetPassword(
            email,
        );
    }

    resetPassword(
        token: string,
        password: string,
        password_confirmation: string,
    ): ResultAsync<true, FallbackError> {
        return this.authenticationRemoteDatasource.resetPassword(
            token,
            password,
            password_confirmation,
        );
    }
}
